/**
 * <rte-content> - progressive enhancement af merchant-forfattet RTE-HTML.
 *
 * Navnet er bevidst IKKE <rich-text>: Dawn og Horizon har en SEKTION der
 * hedder rich-text, og to ting med samme navn i to namespaces forvirrer den
 * naeste laeser. rte-content siger hvad det er - adfaerdslaget om .rte.
 *
 * Eneste opgave: brede tabeller. En <table> vokser forbi sin container naar
 * celleindholdets min-content er bredere, og merchant-indhold kan ikke
 * forudsiges - /pages/size-guide har seks maaltabeller, to af dem paa 12
 * kolonner. Maalt 2026-09-21 ved 390px: hele DOKUMENTET kunne panoreres
 * vandret (document.scrollWidth 1091), altsaa WCAG 2.2 AA 1.4.10 Reflow, og
 * EAA/DE er i scope. Hver tabel pakkes derfor i en .rte__scroller.
 *
 * En scroll-container skal kunne naas med tastatur (WCAG 2.1.1) - ellers kan
 * en tastaturbruger ikke naa de skjulte kolonner. Men KUN naar den faktisk
 * scroller, ellers saar vi tomme tabstops paa hver smal tabel. tabindex
 * saettes derfor efter maaling, og maales igen naar bredden aendrer sig
 * (ResizeObserver) og efter document.fonts.ready, hvor kolonnebredderne kan
 * skifte med webfonten. role/aria-label haenger IKKE sammen med tabindex:
 * tastaturadgang afhaenger af om der scrolles, navnet af om vi har et.
 *
 * Progressive enhancement: uden JS staar tabellen praecis som foer - server-
 * HTML'en er fuldt laesbar, kun mobilens vandrette scroll bliver tilbage.
 * Pakningen er idempotent, saa shopify:section:load i editoren ikke
 * dobbeltpakker.
 */
class RteContent extends HTMLElement {
  connectedCallback() {
    this.scrollers = this.#wrapTables();
    if (!this.scrollers.length) return;

    this.#measure();
    this.ro = new ResizeObserver(() => this.#measure());
    for (const { el } of this.scrollers) this.ro.observe(el);
    document.fonts?.ready.then(() => {
      if (this.isConnected) this.#measure();
    });
  }

  disconnectedCallback() {
    this.ro?.disconnect();
  }

  #wrapTables() {
    const affordance = this.dataset.textTable || '';
    const scrollers = [];

    for (const table of this.querySelectorAll('table')) {
      let el = table.parentElement;
      if (!el.classList.contains('rte__scroller')) {
        el = document.createElement('div');
        el.className = 'rte__scroller';
        table.replaceWith(el);
        el.append(table);
      }
      // <caption> er tabellens egen titel og skiller seks ens regioner paa
      // size-guiden fra hinanden - men den fortaeller ikke at regionen kan
      // rulles, saa affordancen laegges EFTER navnet i stedet for at
      // erstatte det. Ingen caption: kun affordancen.
      const caption = table.querySelector(':scope > caption')?.textContent.trim();
      const label = caption ? `${caption}. ${affordance}` : affordance;
      scrollers.push({ el, label });
    }

    return scrollers;
  }

  #measure() {
    for (const { el, label } of this.scrollers) {
      const scrolls = el.scrollWidth > el.clientWidth + 1;

      if (scrolls) {
        el.setAttribute('tabindex', '0');
        if (label) {
          el.setAttribute('role', 'region');
          el.setAttribute('aria-label', label);
        }
        continue;
      }
      // Tag aldrig tabindex fra et element der HAR fokus - fokus ville falde
      // til <body>, og brugeren ville miste sin plads midt i dokumentet.
      if (document.activeElement === el) continue;
      el.removeAttribute('tabindex');
      el.removeAttribute('role');
      el.removeAttribute('aria-label');
    }
  }
}

if (!customElements.get('rte-content')) {
  customElements.define('rte-content', RteContent);
}
